// bootRedirectQuery.js — preserve the live query string + hash across the
// shell's boot-time / SPA-nav bounce-to-'/' redirects.
//
// tickets/opus.md "the router STRIPS the query string during boot redirect"
// — root cause: `window.location.pathname` never carries `?…`/`#…`, so any
// redirect that rebuilds its target purely from pathname silently drops
// every query param (the analyze-debug lever `?analyze=debug`, a claim flow
// `?claim=credits`, anything hand-appended for repro). Confirmed against the
// smbls router plugin (smbls/plugins/router/index.js `router()`): the
// target path handed to `el.router(...)` is parsed with
// `new URL(origin + path)` and `search`/`hash` are read straight off that
// parse, then threaded into both `state.query` and the `pushState` URL — so
// a query-bearing path round-trips correctly with no extra history re-apply
// step needed. `el.router()` DOES accept (and preserve) a query-bearing
// path; it just has to be GIVEN one.
//
// There were THREE call sites in this package rebuilding a bare `'/'` on a
// boot/session bounce, all fixed the same way (see functions/
// bootRedirectQuery.test.js for the acceptance shape of each):
//   • app.js `_leaveModuleRouteForSwitch` — module-route leave on tenant
//     switch (`el.router('/', el)`).
//   • app.js `_guardDemoRoute` — the reactive demo-lockdown fallback
//     (`el.router('/', el.getRoot())`).
//   • functions/demoRouteGuard.js `demoRouteGuard` — the pre-mount smbls
//     router GUARD (returns a redirect STRING, not an `el.router()` call —
//     same bug class, different shape, so it reattaches `query`/`hash` from
//     its own `guardContext` instead of `window.location`).

// Used by the two `el.router()` call sites — reads the OUTGOING (current)
// window location, since the navigation target is a bare `'/'` and the
// query/hash being preserved is whatever the visitor already had on the
// address bar.
export const withLiveQuery = (path, win = typeof window !== 'undefined' ? window : null) => {
  if (!win?.location) return path
  return path + (win.location.search || '') + (win.location.hash || '')
}

// Used by demoRouteGuard.js — the guard receives the smbls router's OWN
// parse of the path being NAVIGATED TO (guardContext.query/hash), not the
// page being left, so it can't reuse `withLiveQuery` above (which reads
// `window.location`, the page being left). `query` is the router's already
// -parsed object (see plugins/router/index.js `parseQuery`); `hash`
// includes its leading `#` already (URL API `.hash` getter contract).
export const queryHashTail = (query, hash) => {
  const qs = query && Object.keys(query).length ? '?' + new URLSearchParams(query).toString() : ''
  return qs + (hash || '')
}
